import {useCallback} from "react"

import {
    createEphemeralAppFromTemplate,
    createWorkflowFromEphemeralAtom,
    generateSlug,
    loadAgentTemplateFromEphemeralAtom,
    type AgentSetupSelection,
    type AgentStarterTemplate,
    type AgentTemplateSource,
} from "@agenta/entities/workflow"
import {localize} from "@agenta/i18n"
import {extractApiErrorMessage} from "@agenta/shared/utils"
import {useSetAtom} from "jotai"

export interface CreatedAgent {
    appId: string
    revisionId: string
    name: string
    createdAt?: string | null
    createdById?: string | null
    sessionId?: string
    executionId?: string
    inputId?: string
    replayed?: boolean
}

export interface CreateAgentParams {
    /** Agent name; defaults to the ephemeral factory's own. */
    name?: string
    /**
     * Commit THIS existing ephemeral (`local-*`) instead of minting a fresh one. Used by
     * playground-native onboarding, which already minted the ephemeral to render the shell.
     */
    entityId?: string
    /** Starter cards load their package and begin the durable first session in one request. */
    template?: AgentStarterTemplate
    /** A package from outside the catalog (an upload or a GitHub repository). */
    source?: AgentTemplateSource
    /** The host's current template prompt, including any edits made before creation. */
    stagingSessionId?: string
    attachmentIds?: string[]
    initialMessage?: string
    /** Account choices made in the starter setup step. */
    setup?: AgentSetupSelection
}

export interface UseCreateAgentOptions {
    /** Surface the failure however the host does (antd message, a toast, an inline error). */
    onError?: (message: string) => void
}

/**
 * Mint an ephemeral agent, commit it, and hand back the real ids.
 *
 * The commit is what turns a `local-*` draft into an agent with an app id, so every create path
 * on every surface goes through it. What happens NEXT — refreshing a roster cache, stashing a
 * first-run seed, where to navigate — is the host's, and stays out of here.
 *
 * A re-entry latch protects every caller (home button, composer, template cards) from a rapid
 * double-click minting two agents; the UI-level disabled/loading guards don't cover every path.
 * It is MODULE-scoped on purpose: a ref would only latch one component, and the double-mint this
 * guards against is two surfaces firing at once (a template card under the home button) as much
 * as one button pressed twice.
 */
let creating = false

export const useCreateAgent = ({onError}: UseCreateAgentOptions = {}) => {
    const commitFromEphemeral = useSetAtom(createWorkflowFromEphemeralAtom)
    const loadTemplateFromEphemeral = useSetAtom(loadAgentTemplateFromEphemeralAtom)

    return useCallback(
        async ({
            name,
            entityId,
            template,
            source,
            initialMessage,
            stagingSessionId,
            attachmentIds,
            setup,
        }: CreateAgentParams = {}): Promise<CreatedAgent | null> => {
            if (creating) return null
            creating = true
            try {
                const agentName = name?.trim() || "New agent"
                const ephemeralId =
                    entityId ??
                    (await createEphemeralAppFromTemplate({
                        type: "agent",
                        defaultName: agentName,
                    }))
                if (!ephemeralId) {
                    onError?.(localize("com_home_create_agent_start_error"))
                    return null
                }

                if (template || source) {
                    const result = await loadTemplateFromEphemeral({
                        revisionId: ephemeralId,
                        template,
                        source,
                        initialMessage,
                        stagingSessionId,
                        attachmentIds,
                        setup,
                    })
                    return {
                        appId: result.workflow_id,
                        revisionId: result.revision_id,
                        name: agentName,
                        sessionId: result.session_id,
                        executionId: result.execution_id,
                        inputId: result.input_id,
                        replayed: result.replayed,
                    }
                }

                // Slug must be unique per project — the default slug ("agent") collides on every
                // create now that the name is no longer typed by the user.
                const uniqueSuffix = `${Date.now().toString(36)}${Math.floor(Math.random() * 1296)
                    .toString(36)
                    .padStart(2, "0")}`
                const slug = `${generateSlug(agentName) || "agent"}-${uniqueSuffix}`

                const result = await commitFromEphemeral({
                    revisionId: ephemeralId,
                    name: agentName,
                    slug,
                })
                if (!result.success) {
                    onError?.(extractApiErrorMessage(result.error))
                    return null
                }

                const appId = result.workflow?.workflow_id ?? result.workflow?.id
                const revisionId = result.newRevisionId
                if (!appId || !revisionId) {
                    onError?.(localize("com_home_create_agent_open_error"))
                    return null
                }

                return {
                    appId,
                    revisionId,
                    name: agentName,
                    createdAt: result.workflow?.created_at ?? null,
                    createdById: result.workflow?.created_by_id ?? null,
                }
            } catch (error) {
                onError?.(extractApiErrorMessage(error))
                return null
            } finally {
                creating = false
            }
        },
        [commitFromEphemeral, loadTemplateFromEphemeral, onError],
    )
}
